import { PRUNING_STYLES, type PruningStyle } from '@/flora/FloraInstance';
import {
  DEFAULT_TAG_RADIUS_METERS,
  MAX_TAG_LABEL_LENGTH,
  MAX_TAG_NOTE_LENGTH,
  MAX_TAG_RADIUS_METERS,
  TAG_CATEGORIES,
  TAG_EMPHASES,
  type TagCategory,
  type TagEmphasis,
} from '@/semantics/SemanticTag';
import { TAG_IMAGE_INTENTS, type TagImageIntent } from '@/semantics/TagImage';
import { SIMPLE_SEASONS, WEATHER_PRESETS, type SimpleSeason, type WeatherPreset } from '@/environment/EnvironmentState';
import { SitePlanFormatError, type PlanPlanting } from '@/reference/SitePlan';
import {
  parseFloraArtifact,
  parseStructureArtifact,
  parseSurfaceArtifact,
  parseTerrainArtifact,
  type PlanStructure,
  type SurfaceRegionPlan,
  type TerrainControlPoint,
} from '@/reference/SitePipeline';

/**
 * What the wand is allowed to do to a site, and how to read a model's reply as a list of those
 * things.
 *
 * ## Why a list of operations rather than a document
 *
 * The reference pipeline already banked the load-bearing decision: **the model returns a plan, not a
 * document**. The wand inherits it and adds the one thing a plan cannot express — that this is an
 * *edit of a site that already exists*. So a spell is a short list of typed operations applied to the
 * live project, not a new project that replaces it. That difference is what makes "make it a bit
 * smaller" a coherent request: there is something to be smaller than.
 *
 * ## Why these operations and no others
 *
 * Every op maps onto an edit the user could already have made by hand with an existing tool, and is
 * compiled by the code that tool uses. The wand adds no capability; it adds a way of *asking*. That
 * is deliberate and it is the safety property: a spell can produce nothing the editor cannot undo,
 * re-render, serialize, and show in the inspector, because at the point of application it is the same
 * command a mouse would have produced.
 *
 * | op | hand equivalent | compiled by |
 * | -- | --------------- | ----------- |
 * | `sculpt` | the sculpt brush | control points blended into the heightfield |
 * | `paint` | the paint brush | polygons rasterized into the splat map |
 * | `plant` | the placement tool | the shared placement engine |
 * | `remove-plants` / `adjust-plants` | select + Delete / the inspector | flora commands |
 * | `build` / `remove-structures` | (site objects) | the pipeline's structure builder |
 * | `environment` | the environment tray | the environment manager |
 *
 * ## Parsing
 *
 * Forgiving about values, strict about structure — the same rule `parseSitePlan` runs on, for the
 * same reason: a paid call should not be discarded over one out-of-range number, but a spell that
 * silently applies nothing is worse than an error. The per-op readers are the pipeline's own
 * (`parseFloraArtifact`, `parseStructureArtifact`, …) rather than second copies, so a planting mass
 * means exactly the same thing whichever feature produced it.
 *
 * An unrecognised op kind is **dropped and reported**, not fatal. A model that invents a sixth
 * operation alongside three good ones should still get the three applied, with the invention named.
 */

export const WAND_SCHEMA_VERSION = 1;

/* --------------------------------- Targeting --------------------------------- */

/**
 * Which existing plants an op acts on.
 *
 * Several selectors may be given; they intersect. `selected` is resolved by the compiler against the
 * live selection rather than being written out as ids, so "make these shorter" survives the user
 * changing their mind between the click and the reply.
 */
export interface PlantTarget {
  /** Explicit instance ids, as listed in the context brief. */
  ids?: string[];
  /** Catalog species ids. */
  speciesIds?: string[];
  /** Everything within a radius of a world point. */
  near?: { x: number; z: number; radiusMeters: number };
  /**
   * Everything inside a world polygon — the outline the designer drew, most of the time.
   *
   * Distinct from `near` rather than folded into it because "clear this area" about a hand-drawn
   * shape has an exact answer, and a circle around the shape's centroid is not it: it takes out
   * plants outside the outline and leaves plants inside it.
   */
  within?: [number, number][];
  /** The user's current selection. */
  selected?: boolean;
  /** Cap on how many are affected, nearest to `near` first. Guards a runaway "remove the trees". */
  limit?: number;
}

/** The subset of instance fields the wand may edit. Position is deliberately absent — see below. */
export interface PlantPatch {
  maturity?: number;
  fullness?: number;
  health?: number;
  pruningStyle?: PruningStyle;
  seasonOverrideDayOfYear?: number;
  /** Degrees. Applied to the Y axis only; a plant does not lie on its side. */
  rotationDegrees?: number;
  hidden?: boolean;
  locked?: boolean;
}

/* ----------------------------------- Ops ----------------------------------- */

/**
 * Reshape the ground **inside a shape**, rather than around a point.
 *
 * A control point is a dome, and a dome is the wrong primitive for "make this area a berm" once the
 * designer has drawn the area: approximating a hand-drawn outline with overlapping circles gives
 * back a lumpy version of a shape that was already exact. So an area drives the ground to one
 * elevation everywhere inside its outline, and fades to nothing over `falloffMeters` outside it —
 * the same smoothstep the points use, measured from the boundary instead of from a centre.
 */
export interface SculptArea {
  polygon: [number, number][];
  elevationMeters: number;
  /** How far outside the outline the change fades to nothing. */
  falloffMeters: number;
  notes?: string;
}

/**
 * Reshape the ground.
 *
 * `mode` is the whole reason this is not just the pipeline's terrain artifact: a fresh site has no
 * ground to be relative to, an existing one does. `relative` adds to what is there (raise a berm on a
 * slope and the slope survives); `absolute` drives toward a level (flatten a terrace).
 */
export interface SculptOp {
  kind: 'sculpt';
  mode: 'relative' | 'absolute';
  points: TerrainControlPoint[];
  /** Shapes to raise or level as a whole. Merges with `points` into one landform. */
  areas?: SculptArea[];
  notes?: string;
}

/** Paint ground materials — grass, mulch, water, blacktop, gravel. */
export interface PaintOp {
  kind: 'paint';
  regions: SurfaceRegionPlan[];
  notes?: string;
}

/** Add planting, described as masses and expanded by the shared placement engine. */
export interface PlantOp {
  kind: 'plant';
  plantings: PlanPlanting[];
  notes?: string;
}

export interface RemovePlantsOp {
  kind: 'remove-plants';
  target: PlantTarget;
  notes?: string;
}

export interface AdjustPlantsOp {
  kind: 'adjust-plants';
  target: PlantTarget;
  patch: PlantPatch;
  notes?: string;
}

export interface BuildOp {
  kind: 'build';
  structures: PlanStructure[];
  notes?: string;
}

export interface RemoveStructuresOp {
  kind: 'remove-structures';
  ids: string[];
  notes?: string;
}

/**
 * Say what something *is*, so a render knows how to draw it.
 *
 * The one op that adds no geometry, and the one that most changes what comes out of an enhanced
 * render. It exists because the wand kept hitting the same wall the site generator does: the
 * document can hold a fence's footprint and its height, and nothing about it being a split-rail
 * fence in silvered cedar — so a spell that builds a fence produces a grey box that the render then
 * reinvents as whatever it likes.
 *
 * The rule that keeps this honest is the same one every other op obeys: a `tag` op compiles to
 * exactly the `AddTagCommand` the tag tool produces when a person types a tag by hand. The wand adds
 * no capability here either; it types the tag for you, in the same document, on the same history.
 *
 * ## Binding to what the same spell just built
 *
 * A tag has to name its subject, and the subject of "tag the fence you just built" does not have an
 * id yet — the compiler mints those, and deliberately reissues them so a spell cannot collide with
 * an object the user placed earlier. So a tag op refers to a structure by the **plan id the build op
 * used** (`structureRef: "fence-1"`), and the compiler resolves it against the ids it minted in this
 * same spell. That is the passthrough: describe a thing and say how it looks, in one turn.
 *
 * `attachImageIndex` is the other half. When the designer attached photographs to the request, the
 * spell may point a tag at one of them by position, and the compiler moves that picture out of the
 * turn and onto the tag — where it lives in the document and reaches every future render, instead of
 * evaporating when the bubble closes.
 */
export interface TagOp {
  kind: 'tag';
  tags: PlanTag[];
  notes?: string;
}

export interface PlanTag {
  /** What the tag says. Short noun phrase. */
  label: string;
  category: TagCategory;
  emphasis: TagEmphasis;
  note?: string;
  /** Plan id of a structure created by a `build` op in this same spell. */
  structureRef?: string;
  /** Id of a site object that already exists. */
  structureId?: string;
  /** Id of an existing plant. */
  plantId?: string;
  /** A painted ground material this statement is about. */
  groundLayer?: string;
  /** A place on the ground, in world metres. */
  location?: { x: number; z: number; radiusMeters: number };
  /** Whole-site register. */
  site?: boolean;
  /**
   * Which attached photograph this tag is about, 1-based, as numbered in the request. Out-of-range
   * values are dropped rather than clamped: attaching the wrong picture to a tag is worse than
   * attaching none, and a model that miscounted has no idea which one it meant.
   */
  attachImageIndex?: number;
  /** How the attached picture should be read. Defaults to `match`. */
  imageIntent?: TagImageIntent;
}

/** Move the clock, the calendar, or the weather. Every field is optional; absent means unchanged. */
export interface EnvironmentOp {
  kind: 'environment';
  hour?: number;
  minute?: number;
  dayOfYear?: number;
  season?: SimpleSeason;
  weather?: WeatherPreset;
  latitudeDegrees?: number;
  notes?: string;
}

export type WandOp =
  | SculptOp
  | PaintOp
  | PlantOp
  | RemovePlantsOp
  | AdjustPlantsOp
  | BuildOp
  | RemoveStructuresOp
  | TagOp
  | EnvironmentOp;

export type WandOpKind = WandOp['kind'];

export const WAND_OP_KINDS: readonly WandOpKind[] = [
  'sculpt',
  'paint',
  'plant',
  'remove-plants',
  'adjust-plants',
  'build',
  'remove-structures',
  'tag',
  'environment',
];

/* ---------------------------------- Spell ---------------------------------- */

/**
 * One reply from the wand.
 *
 * `questions` is not a courtesy. A request like "make this corner nicer" has no defensible reading,
 * and a model that guesses at it produces a change the user then has to *find* in order to undo.
 * Coming back with two short questions and applying nothing is the better outcome, so the schema
 * makes it a first-class answer rather than something to be smuggled into `summary`.
 */
export interface WandSpell {
  /** One sentence, past tense, in the wand's own voice. Shown as the result headline. */
  summary: string;
  ops: WandOp[];
  /** Asked instead of acting when the request is ambiguous. When non-empty, `ops` should be empty. */
  questions: string[];
  /** What was inferred rather than told — substitutions, assumptions, things not represented. */
  notes: string;
  /** The model's own confidence, `0..1`. Surfaced, never acted on. */
  confidence: number;
  /** Op kinds that arrived but could not be read. Reported so a dropped op is never silent. */
  dropped: string[];
}

/** Thrown only when the reply is not a spell at all. Bad *values* are clamped, not thrown. */
export class WandFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WandFormatError';
  }
}

export function parseWandSpell(raw: unknown): WandSpell {
  const data = asRecord(raw, 'spell');
  const dropped: string[] = [];
  const ops: WandOp[] = [];

  for (const [index, entry] of asArray(data.ops ?? [], 'spell.ops').entries()) {
    const op = parseOp(entry, index, dropped);
    if (op) ops.push(op);
  }

  const questions = asArray(data.questions ?? [], 'spell.questions')
    .map((entry) => text(entry))
    .filter((entry): entry is string => entry !== undefined)
    // Three is the point at which a clarifying question stops being help and becomes a form.
    .slice(0, 3);

  return {
    summary: text(data.summary) ?? (questions.length > 0 ? 'The wand needs to know more.' : 'Nothing changed.'),
    ops,
    questions,
    notes: text(data.notes) ?? '',
    confidence: clamp(number(data.confidence, 0.5), 0, 1),
    dropped,
  };
}

function parseOp(raw: unknown, index: number, dropped: string[]): WandOp | null {
  const where = `spell.ops[${index}]`;
  let data: Record<string, unknown>;
  try {
    data = asRecord(raw, where);
  } catch {
    dropped.push(`ops[${index}] was not an object`);
    return null;
  }

  const kind = typeof data.kind === 'string' ? data.kind : '';
  const notes = text(data.notes);
  const withNotes = <T extends WandOp>(op: T): T => (notes ? { ...op, notes } : op);

  try {
    switch (kind) {
      case 'sculpt': {
        const terrain = parseTerrainArtifact({ controlPoints: data.points ?? data.controlPoints ?? [] });
        const areas = asArray(data.areas ?? [], `${where}.areas`)
          .map((entry) => parseSculptArea(entry))
          .filter((entry): entry is SculptArea => entry !== null);
        if (terrain.controlPoints.length === 0 && areas.length === 0) {
          dropped.push('a sculpt op with no usable control points or areas');
          return null;
        }
        return withNotes<SculptOp>({
          kind: 'sculpt',
          mode: data.mode === 'absolute' ? 'absolute' : 'relative',
          points: terrain.controlPoints,
          ...(areas.length > 0 ? { areas } : {}),
        });
      }
      case 'paint': {
        const surfaces = parseSurfaceArtifact({ regions: data.regions ?? [] });
        if (surfaces.regions.length === 0) {
          dropped.push('a paint op with no usable region');
          return null;
        }
        return withNotes<PaintOp>({ kind: 'paint', regions: surfaces.regions });
      }
      case 'plant': {
        const flora = parseFloraArtifact({ plantings: data.plantings ?? [] });
        if (flora.plantings.length === 0) {
          dropped.push('a plant op with no planting masses');
          return null;
        }
        return withNotes<PlantOp>({ kind: 'plant', plantings: flora.plantings });
      }
      case 'remove-plants': {
        const target = parseTarget(data.target, `${where}.target`);
        if (!target) {
          dropped.push('a remove-plants op that named no plants');
          return null;
        }
        return withNotes<RemovePlantsOp>({ kind: 'remove-plants', target });
      }
      case 'adjust-plants': {
        const target = parseTarget(data.target, `${where}.target`);
        if (!target) {
          dropped.push('an adjust-plants op that named no plants');
          return null;
        }
        const patch = parsePatch(data.patch);
        if (Object.keys(patch).length === 0) {
          dropped.push('an adjust-plants op that changed nothing');
          return null;
        }
        return withNotes<AdjustPlantsOp>({ kind: 'adjust-plants', target, patch });
      }
      case 'build': {
        const artifact = parseStructureArtifact({ structures: data.structures ?? [] });
        if (artifact.structures.length === 0) {
          dropped.push('a build op with no structures');
          return null;
        }
        return withNotes<BuildOp>({ kind: 'build', structures: artifact.structures });
      }
      case 'remove-structures': {
        const ids = asArray(data.ids ?? [], `${where}.ids`)
          .map((entry) => text(entry))
          .filter((entry): entry is string => entry !== undefined);
        if (ids.length === 0) {
          dropped.push('a remove-structures op that named nothing');
          return null;
        }
        return withNotes<RemoveStructuresOp>({ kind: 'remove-structures', ids });
      }
      case 'tag': {
        const tags = asArray(data.tags ?? [], `${where}.tags`)
          .map((entry) => parseTag(entry))
          .filter((entry): entry is PlanTag => entry !== null);
        if (tags.length === 0) {
          dropped.push('a tag op with no readable tags');
          return null;
        }
        return withNotes<TagOp>({ kind: 'tag', tags });
      }
      case 'environment':
        return withNotes(parseEnvironmentOp(data, dropped));
      default:
        dropped.push(kind.length > 0 ? `an unknown operation “${kind}”` : 'an operation with no kind');
        return null;
    }
  } catch (error) {
    // One malformed op among four must not cost the other three. The pipeline reached the same
    // conclusion about polygons for the same reason: a paid call is expensive to re-earn.
    dropped.push(
      `${kind || 'an'} operation could not be read${error instanceof SitePlanFormatError ? ` (${error.message})` : ''}`,
    );
    return null;
  }
}

function parseEnvironmentOp(data: Record<string, unknown>, dropped: string[]): EnvironmentOp {
  const op: EnvironmentOp = { kind: 'environment' };
  if (data.hour !== undefined) op.hour = clampInt(number(data.hour, 12), 0, 23);
  if (data.minute !== undefined) op.minute = clampInt(number(data.minute, 0), 0, 59);
  if (data.dayOfYear !== undefined) op.dayOfYear = clampInt(number(data.dayOfYear, 172), 1, 365);
  if (SIMPLE_SEASONS.includes(data.season as SimpleSeason)) op.season = data.season as SimpleSeason;
  if (WEATHER_PRESETS.includes(data.weather as WeatherPreset)) op.weather = data.weather as WeatherPreset;
  if (data.latitudeDegrees !== undefined) {
    op.latitudeDegrees = clamp(number(data.latitudeDegrees, 45), -90, 90);
  }
  if (Object.keys(op).length === 1) dropped.push('an environment op that set nothing');
  return op;
}

/**
 * Read one sculpt area.
 *
 * The elevation range matches the control points' own: a sculpt that can raise a 3 m mound at a
 * point should not be refused for asking the same of a shape. Falloff has a floor because a
 * zero-metre fade is a cliff, and a cliff around a hand-drawn bed is never what was meant.
 */
function parseSculptArea(raw: unknown): SculptArea | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const data = raw as Record<string, unknown>;

  const polygon = parseRing(data.polygon);
  if (!polygon) return null;

  const area: SculptArea = {
    polygon,
    elevationMeters: clamp(number(data.elevationMeters, 0), -20, 20),
    falloffMeters: clamp(number(data.falloffMeters, 1.5), 0.25, 30),
  };
  const notes = text(data.notes);
  if (notes) area.notes = notes;
  return area;
}

/** A closed ring in either `[[x, z], …]` or `[{x, z}, …]` form. Fewer than three vertices is null. */
function parseRing(raw: unknown): [number, number][] | null {
  if (!Array.isArray(raw)) return null;
  const points: [number, number][] = [];
  for (const entry of raw) {
    if (Array.isArray(entry) && entry.length >= 2) {
      points.push([number(entry[0], 0), number(entry[1], 0)]);
    } else if (entry && typeof entry === 'object') {
      const point = entry as Record<string, unknown>;
      points.push([number(point.x, 0), number(point.z ?? point.y, 0)]);
    }
  }
  return points.length >= 3 ? points : null;
}

/**
 * Read one tag from a spell.
 *
 * Returns null when the tag has nothing to say or nothing to say it about. Both halves are required
 * and neither has a defensible default: a tag with no label is a blank line in every future prompt,
 * and a tag with no target would have to be guessed onto something — which is how a statement about
 * a fence ends up attached to the whole site.
 */
function parseTag(raw: unknown): PlanTag | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const data = raw as Record<string, unknown>;

  const label = text(data.label);
  if (!label) return null;

  const tag: PlanTag = {
    label: label.slice(0, MAX_TAG_LABEL_LENGTH),
    category: TAG_CATEGORIES.includes(data.category as TagCategory)
      ? (data.category as TagCategory)
      : 'identity',
    emphasis: TAG_EMPHASES.includes(data.emphasis as TagEmphasis)
      ? (data.emphasis as TagEmphasis)
      : 'mention',
  };

  const note = text(data.note);
  if (note) tag.note = note.slice(0, MAX_TAG_NOTE_LENGTH);

  const structureRef = text(data.structureRef);
  const structureId = text(data.structureId);
  const plantId = text(data.plantId);
  const groundLayer = text(data.groundLayer);
  if (structureRef) tag.structureRef = structureRef;
  if (structureId) tag.structureId = structureId;
  if (plantId) tag.plantId = plantId;
  if (groundLayer) tag.groundLayer = groundLayer;

  if (data.location !== undefined && data.location !== null && typeof data.location === 'object') {
    const location = data.location as Record<string, unknown>;
    tag.location = {
      x: number(location.x, 0),
      z: number(location.z, 0),
      radiusMeters: clamp(number(location.radiusMeters, DEFAULT_TAG_RADIUS_METERS), 0.25, MAX_TAG_RADIUS_METERS),
    };
  }

  if (data.site === true) tag.site = true;

  if (data.attachImageIndex !== undefined) {
    const index = Math.trunc(number(data.attachImageIndex, 0));
    if (index >= 1) tag.attachImageIndex = index;
  }
  if (TAG_IMAGE_INTENTS.includes(data.imageIntent as TagImageIntent)) {
    tag.imageIntent = data.imageIntent as TagImageIntent;
  }

  const targeted =
    tag.structureRef !== undefined ||
    tag.structureId !== undefined ||
    tag.plantId !== undefined ||
    tag.groundLayer !== undefined ||
    tag.location !== undefined ||
    tag.site === true;
  return targeted ? tag : null;
}

/** Read a plant selector. Returns null when it names nothing — which must never mean "everything". */
function parseTarget(raw: unknown, where: string): PlantTarget | null {
  if (raw === undefined || raw === null) return null;
  const data = asRecord(raw, where);
  const target: PlantTarget = {};

  const ids = Array.isArray(data.ids)
    ? data.ids.map((entry) => text(entry)).filter((entry): entry is string => entry !== undefined)
    : [];
  if (ids.length > 0) target.ids = ids;

  const species = Array.isArray(data.speciesIds)
    ? data.speciesIds.map((entry) => text(entry)).filter((entry): entry is string => entry !== undefined)
    : [];
  if (species.length > 0) target.speciesIds = species;

  if (data.near !== undefined && data.near !== null) {
    const near = asRecord(data.near, `${where}.near`);
    target.near = {
      x: number(near.x, 0),
      z: number(near.z, 0),
      radiusMeters: clamp(number(near.radiusMeters, 3), 0.1, 500),
    };
  }

  const within = parseRing(data.within ?? data.polygon);
  if (within) target.within = within;

  if (data.selected === true) target.selected = true;
  if (data.limit !== undefined) target.limit = clampInt(number(data.limit, 1), 1, 5000);

  return Object.keys(target).length === 0 ? null : target;
}

function parsePatch(raw: unknown): PlantPatch {
  if (raw === undefined || raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const data = raw as Record<string, unknown>;
  const patch: PlantPatch = {};

  // 1.5 rather than 1 is the catalog's own "exceptional specimen" ceiling; the compiler still holds
  // each plant to whether its species permits overgrowth.
  if (data.maturity !== undefined) patch.maturity = clamp(number(data.maturity, 1), 0, 1.5);
  if (data.fullness !== undefined) patch.fullness = clamp(number(data.fullness, 1), 0, 1);
  if (data.health !== undefined) patch.health = clamp(number(data.health, 1), 0, 1);
  if (PRUNING_STYLES.includes(data.pruningStyle as PruningStyle)) {
    patch.pruningStyle = data.pruningStyle as PruningStyle;
  }
  if (data.seasonOverrideDayOfYear !== undefined) {
    patch.seasonOverrideDayOfYear = clampInt(number(data.seasonOverrideDayOfYear, 172), 1, 365);
  }
  if (data.rotationDegrees !== undefined) {
    patch.rotationDegrees = wrap(number(data.rotationDegrees, 0), 360);
  }
  if (typeof data.hidden === 'boolean') patch.hidden = data.hidden;
  if (typeof data.locked === 'boolean') patch.locked = data.locked;

  return patch;
}

/* --------------------------------- Helpers --------------------------------- */

function asRecord(value: unknown, where: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new WandFormatError(`Expected ${where} to be an object.`);
  }
  return value as Record<string, unknown>;
}

function asArray(value: unknown, where: string): unknown[] {
  if (!Array.isArray(value)) throw new WandFormatError(`Expected ${where} to be a list.`);
  return value;
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function number(value: unknown, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clampInt(value: number, min: number, max: number): number {
  return clamp(Math.trunc(value), min, max);
}

function wrap(value: number, range: number): number {
  return ((value % range) + range) % range;
}
